<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :ajax-params="ajaxParams"
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
            :showHeader="showTableHeader"
            :pagination="paginateTable"
            ajax-path="tpp/domains"
            @rowSelected="handleRowSelected"     
        />
        <app-modal
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showRegisterModal"
        >
            <template slot="header">Register a Domain</template>
            <template slot="body">
                <app-input
                    v-model="registerForm.Domain"
                    placeholder="Domain Name"
                    :error="registerForm.errors.get('Domain')"
                    @updated="registerForm.errors.clear('Domain')"
                    focus
                    required
                    :loading="checkingDomainAvailability"
                    @keyup.native="checkDomainAvailability(registerForm.Domain)"
                    :checked="domainIsAvailable"
                />
                <app-select 
                    v-model="registerForm.Period"
                    placeholder="Period"
                    :options="periodOptions"
                    label-key="label"
                    value-key="value"
                    v-if="domainIsAvailable && !registerForm.au"
                />
                <div class="mt-5" v-if="registerForm.au && domainIsAvailable">
                    <div class="mb-4">.au <span class="font-thin tracking-widest uppercase">Eligibility Details</span></div>
                    <app-input 
                        v-model="registerForm.RegistrantName"
                        placeholder="Registrant Name"
                        :error="registerForm.errors.get('RegistrantName')"
                        @updated="registerForm.error.clear('RegistrantName')"
                        required
                    />
                    <app-select
                        v-model="registerForm.RegistrantIDType"
                        placeholder="Registrant ID Type"
                        :error="registerForm.errors.get('RegistrantIDType')"
                        @updated="registerForm.errors.clear('RegistrantIDType')"
                        :options="[                            
                            { code: 1, label: 'Australian Company Number (ACN)'},
                            { code: 12, label: 'Australian Business Number (ABN).'},
                            { code: 2, label: 'ACT Business Number'},
                            { code: 3, label: 'NSW Business Number'},
                            { code: 4, label: 'NT Business Number'},
                            { code: 5, label: 'QLD Business Number'},
                            { code: 6, label: 'SA Business Number'},
                            { code: 7, label: 'TAS Business Number'},
                            { code: 8, label: 'VIC Business Number'},
                            { code: 9, label: 'WA Business Number'},
                            { code: 10, label: 'Trademark (TM)'},
                            { code: 11, label: 'Other - Used to record an Incorporated Association number.'},
                        ]"
                        value-key="code"
                        label-key="label"                        
                        required
                    />
                    <app-input 
                        v-model="registerForm.RegistrantID"
                        placeholder="Registrant ID Number"
                        :error="registerForm.errors.get('RegistrantID')"
                        @updated="registerForm.error.clear('RegistrantID')"
                        required
                    />                    
                    <app-select
                        v-model="registerForm.EligibilityType"
                        placeholder="Eligibility Type"
                        :error="registerForm.errors.get('EligibilityType')"
                        @updated="registerForm.errors.clear('EligibilityType')"
                        :options="[
                            {code: 1, label: 'Charity'},
                            {code: 2, label: 'Citizen/Resident'},
                            {code: 3, label: 'Club'},
                            {code: 4, label: 'Commercial Statutory Body'},
                            {code: 5, label: 'Company'},
                            {code: 6, label: 'Incorporated Association'},
                            {code: 7, label: 'Unincorporated Association'},
                            {code: 8, label: 'Industry Body'},
                            {code: 9, label: 'Non-profit Organisation'},
                            {code: 10, label: 'Other'},
                            {code: 11, label: 'Partnership'},
                            {code: 12, label: 'Pending TM Owner'},
                            {code: 13, label: 'Political Party'},
                            {code: 14, label: 'Registered Business'},
                            {code: 15, label: 'Religious/Church Group'},
                            {code: 16, label: 'Sole Trader'},
                            {code: 17, label: 'Trade Union'},
                            {code: 18, label: 'Trademark Owner'},
                            {code: 19, label: 'Child Care Centre'},
                            {code: 20, label: 'Government School'},
                            {code: 21, label: 'Higher Education Institution'},
                            {code: 22, label: 'National Body'},
                            {code: 23, label: 'Non-Government School'},
                            {code: 24, label: 'Pre-school'},
                            {code: 25, label: 'Research Organisation'},
                            {code: 26, label: 'Training Organisation'},
                        ]"
                        value-key="code"
                        label-key="label"
                        required
                    />
                    <app-select
                        v-model="registerForm.EligibilityReason"
                        placeholder="Eligibility Reason"
                        :error="registerForm.errors.get('EligibilityReason')"
                        @updated="registerForm.errors.clear('EligibilityReason')"
                        :options="[
                            {code: 1, label: 'Domain name is an Exact Match Abbreviation or Acronym of your Entity or Trading Name.'},
                            {code: 2, label: 'Close and substantial connection between the domain name and the operations of your Entity.'},
                        ]"
                        value-key="code"
                        label-key="label"
                        required
                    />
                </div>
                <div class="text-right mt-5" v-if="domainIsAvailable">
                    <app-button class="btn-white"
                        @click="showRegisterModal = false"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        :loading="registeringDomain"
                        @click="store"
                    >Register Domain</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            width="lg:w-1/2"
            :show.sync="showEditModal"
        >
            <template slot="header">Domain Information</template>
            <template slot="body">
                <vue-tabs>
                    <v-tab title="Basic">
                        <div class="mt-5" style="display: grid;grid-gap: 15px;grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));">
                            <div>
                                <div class="mb-2 font-bold">Domain Name</div>
                                <div>{{ selected.name }}</div>
                            </div>
                            <div>
                                <div class="mb-2 font-bold">Domain Password</div>
                                <div class="truncate">{{ selected.password }}</div>
                            </div>
                            <div>
                                <div class="mb-2 font-bold">Domain Lock Status</div>
                                <div v-if="selected.lock_status === 0">Cannot be locked/unlocked</div>
                                <div v-if="selected.lock_status === 1">Unlocked</div>
                                <div v-if="selected.lock_status === 2">Locked</div>
                            </div>
                            <div>
                                <div class="mb-2 font-bold">Expiry Date</div>
                                <div>{{ selected.expiry_date }}</div>
                            </div>
                            <div>
                                <div class="mb-2 font-bold">Name Servers</div>
                                <div v-for="host in selected.nameservers">{{ host.name }}</div>
                            </div>
                        </div>
                    </v-tab>
                    <v-tab title="Registrant" v-if="selected.owner">
                        <div class="mt-5">                            
                            <div class="mt-5" style="display: grid;grid-gap: 15px;grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));">
                                <div>
                                    <div class="mb-2 font-bold">First Name</div>
                                    <div>{{ selected.owner.first_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Last Name</div>
                                    <div>{{ selected.owner.last_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Organisation</div>
                                    <div>{{ selected.owner.organisation_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Email</div>
                                    <div>{{ selected.owner.email }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Phone</div>
                                    <div>{{ selected.owner.phone_number || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Fax</div>
                                    <div>{{ selected.owner.fax_number || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 1</div>
                                    <div>{{ selected.owner.address1 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 2</div>
                                    <div>{{ selected.owner.address2 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 3</div>
                                    <div>{{ selected.owner.address3 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">City</div>
                                    <div>{{ selected.owner.city || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">State</div>
                                    <div>{{ selected.owner.region || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Postal Code</div>
                                    <div>{{ selected.owner.postal_code || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Country</div>
                                    <div>{{ selected.owner.country || '--' }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="text-right mt-5">
                            <app-button
                                class="btn-blue"
                                @click="editContact('registrant')"
                            >Edit</app-button>
                        </div>
                    </v-tab>
                    <v-tab title="Admin" v-if="selected.admin">
                        <div class="mt-5">                            
                            <div class="mt-5" style="display: grid;grid-gap: 15px;grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));">
                                <div>
                                    <div class="mb-2 font-bold">First Name</div>
                                    <div>{{ selected.admin.first_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Last Name</div>
                                    <div>{{ selected.admin.last_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Organisation</div>
                                    <div>{{ selected.admin.organisation_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Email</div>
                                    <div>{{ selected.admin.email }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Phone</div>
                                    <div>{{ selected.admin.phone_number || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Fax</div>
                                    <div>{{ selected.admin.fax_number || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 1</div>
                                    <div>{{ selected.admin.address1 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 2</div>
                                    <div>{{ selected.admin.address2 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 3</div>
                                    <div>{{ selected.admin.address3 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">City</div>
                                    <div>{{ selected.admin.city || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">State</div>
                                    <div>{{ selected.admin.region || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Postal Code</div>
                                    <div>{{ selected.admin.postal_code || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Country</div>
                                    <div>{{ selected.admin.country || '--' }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="text-right mt-5">
                            <app-button
                                class="btn-blue"
                                @click="editContact('admin')"
                            >Edit</app-button>
                        </div>
                    </v-tab>
                    <v-tab title="Technical" v-if="selected.technical">
                        <div class="mt-5">                            
                            <div class="mt-5" style="display: grid;grid-gap: 15px;grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));">
                                <div>
                                    <div class="mb-2 font-bold">First Name</div>
                                    <div>{{ selected.technical.first_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Last Name</div>
                                    <div>{{ selected.technical.last_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Organisation</div>
                                    <div>{{ selected.technical.organisation_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Email</div>
                                    <div>{{ selected.technical.email }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Phone</div>
                                    <div>{{ selected.technical.phone_number || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Fax</div>
                                    <div>{{ selected.technical.fax_number || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 1</div>
                                    <div>{{ selected.technical.address1 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 2</div>
                                    <div>{{ selected.technical.address2 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 3</div>
                                    <div>{{ selected.technical.address3 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">City</div>
                                    <div>{{ selected.technical.city || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">State</div>
                                    <div>{{ selected.technical.region || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Postal Code</div>
                                    <div>{{ selected.technical.postal_code || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Country</div>
                                    <div>{{ selected.technical.country || '--' }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="text-right mt-5">
                            <app-button
                                class="btn-blue"
                                @click="editContact('tech')"
                            >Edit</app-button>
                        </div>
                    </v-tab>
                    <v-tab title="Billing" v-if="selected.billing">
                        <div class="mt-5">                            
                            <div class="mt-5" style="display: grid;grid-gap: 15px;grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));">
                                <div>
                                    <div class="mb-2 font-bold">First Name</div>
                                    <div>{{ selected.billing.first_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Last Name</div>
                                    <div>{{ selected.billing.last_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Organisation</div>
                                    <div>{{ selected.billing.organisation_name }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Email</div>
                                    <div>{{ selected.billing.email }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Phone</div>
                                    <div>{{ selected.billing.phone_number || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Fax</div>
                                    <div>{{ selected.billing.fax_number || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 1</div>
                                    <div>{{ selected.billing.address1 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 2</div>
                                    <div>{{ selected.billing.address2 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Address 3</div>
                                    <div>{{ selected.billing.address3 || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">City</div>
                                    <div>{{ selected.billing.city || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">State</div>
                                    <div>{{ selected.billing.region || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Postal Code</div>
                                    <div>{{ selected.billing.postal_code || '--' }}</div>
                                </div>
                                <div>
                                    <div class="mb-2 font-bold">Country</div>
                                    <div>{{ selected.billing.country || '--' }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="text-right mt-5">
                            <app-button
                                class="btn-blue"
                                @click="editContact('billing')"
                            >Edit</app-button>
                        </div>
                    </v-tab>
                </vue-tabs>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showRenewModal"
            width="lg:w-1/3"
        >
            <template slot="header">Renew {{ selected.name }}</template>
            <template slot="body">
                <app-select 
                    v-model="registerForm.Period"
                    placeholder="Period"
                    :options="periodOptions"
                    label-key="label"
                    value-key="value"
                    :loading="checkingDomainAvailability"
                />
                <div class="text-right mt-5">
                    <app-button class="btn-white"
                        @click="showRenewModal = false"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        :disable="checkingDomainAvailability"
                        :loading="updatingNameServers"
                        @click="updateNameServers"
                    >Renew</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEditNameServersModal"
            width="lg:w-1/3"
        >
            <template slot="header">Edit {{ selected.name }} Name Servers</template>
            <template slot="body">
                <app-textarea
                    v-model="nameServersForm.host"
                    placeholder="Name Servers"
                    :error="nameServersForm.errors.get('host')"
                    @updated="nameServersForm.errors.clear('host')"
                    help-text="The domain name of a name server. Enter multiple name servers on separate lines."
                ></app-textarea>
                <div class="text-right mt-5">
                    <app-button class="btn-white"
                        @click="showEditNameServersModal = false"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        :loading="updatingNameServers"
                        @click="updateNameServers"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEditContactModal"
            width="lg:w-1/3"
        >
            <template slot="header">Edit {{ editContactForm.ContactType }} Contact</template>
            <template slot="body">
                <app-input
                    v-model="editContactForm.FirstName"
                    placeholder="First Name"
                    :error="editContactForm.errors.get('FirstName')"
                    @updated="editContactForm.errors.clear('FirstName')"
                    focus
                    required
                />
                <app-input
                    v-model="editContactForm.LastName"
                    placeholder="Last Name"
                    :error="editContactForm.errors.get('LastName')"
                    @updated="editContactForm.errors.clear('LastName')"
                    required
                />
                <app-input
                    v-model="editContactForm.OrganisationName"
                    placeholder="Organisation"
                    :error="editContactForm.errors.get('OrganisationName')"
                    @updated="editContactForm.errors.clear('OrganisationName')"
                />
                <app-input
                    v-model="editContactForm.Address1"
                    placeholder="Address 1"
                    :error="editContactForm.errors.get('Address1')"
                    @updated="editContactForm.errors.clear('Address1')"
                    required
                />
                <app-input
                    v-model="editContactForm.Address2"
                    placeholder="Address 2"
                    :error="editContactForm.errors.get('Address2')"
                    @updated="editContactForm.errors.clear('Address2')"
                />
                <app-input
                    v-model="editContactForm.Address3"
                    placeholder="Address 3"
                    :error="editContactForm.errors.get('Address3')"
                    @updated="editContactForm.errors.clear('Address3')"
                />
                <app-input
                    v-model="editContactForm.City"
                    placeholder="City"
                    :error="editContactForm.errors.get('City')"
                    @updated="editContactForm.errors.clear('City')"
                    required
                />
                <app-select
                    v-model="editContactForm.Region"
                    placeholder="State"
                    :error="editContactForm.errors.get('Region')"
                    @updated="editContactForm.errors.clear('Region')"
                    :options="regions"
                    label-key="name"
                    value-key="shortCode"
                    required
                />
                <app-select
                    v-model="editContactForm.CountryCode"
                    placeholder="Country"
                    :error="editContactForm.errors.get('CountryCode')"
                    @updated="editContactForm.errors.clear('CountryCode')"
                    :options="countries"
                    label-key="countryName"
                    value-key="countryShortCode"
                    required
                />
                <app-input
                    v-model="editContactForm.PostalCode"
                    placeholder="Postal Code"
                    :error="editContactForm.errors.get('PostalCode')"
                    @updated="editContactForm.errors.clear('PostalCode')"
                />
                <app-input
                    v-model="editContactForm.Email"
                    placeholder="Email"
                    :error="editContactForm.errors.get('Email')"
                    @updated="editContactForm.errors.clear('Email')"
                    required
                />
                <app-input
                    v-model="editContactForm.PhoneNumber"
                    placeholder="Phone"
                    :error="editContactForm.errors.get('PhoneNumber')"
                    @updated="editContactForm.errors.clear('PhoneNumber')"
                    required
                    v-mask="'+## (##) #### ####'"
                />
                <app-input
                    v-model="editContactForm.FaxNumber"
                    placeholder="Fax"
                    :error="editContactForm.errors.get('FaxNumber')"
                    @updated="editContactForm.errors.clear('FaxNumber')"
                />
                <div class="text-right mt-5">
                    <app-button class="btn-white"
                        @click="showEditContactModal = false"
                        :disable="updatingContact"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        :loading="updatingContact"
                        @click="updateContact"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    import Http from '@/helpers/Http';
    import debounce from 'lodash/debounce';
    import countries from '@/countries.js';
    import {mask} from 'vue-the-mask';
    import {VueTabs, VTab} from 'vue-nav-tabs/dist/vue-tabs.js';
    import '@/css/vue-tabs.css'
    
    export default {
        props: {
            showTableHeader: {
                type: Boolean,
                default: true,
            },
            paginateTable: {
                type: Boolean,
                default: true,
            },
            ajaxParams: {
                type: Object,
                default() {
                    return {};
                }
            }
        },
        components: {
            VueTabs,
            VTab,
            EditIcon: () => import('@/icons/EditOutlined.vue'),
            SyncIcon: () => import('@/icons/Sync.vue'),
        },
        directives: {
            mask,
        },
        data() {
            return {
                showRenewModal: false,
                showEditNameServersModal: false,
                showRegisterModal: false,
                showEditModal: false,
                showEditContactModal: false,
                checkingDomainAvailability: false,
                tableIsLoading: false,
                tableLoadingText: null,
                domainIsAvailable: false,
                registeringDomain: false,
                updatingNameServers: false,
                updatingContact: false,
                refreshTable: false,
                countries,
                selected: {
                    owner: {},                    
                    admin: {},
                    billing: {},
                    technical: {},
                    nameservers: [],
                },
                renewForm: new Form({
                    domain: null,
                }),
                registerForm: new Form({
                    Domain: null,
                    Period: null,
                    RegistrantIDType: null,
                    RegistrantID: null,
                    RegistrantName: null,
                    EligibilityType: null,
                    EligibilityReason: null,
                    au: false,
                }),
                nameServersForm: new Form({
                    domainId: null,
                    host: null,
                }),
                editContactForm: new Form({
                    DomainId: null,
                    ContactType: null,
                    FirstName: null,
                    LastName: null,
                    OrganisationName: null,
                    Address1: null,
                    Address2: null,
                    Address3: null,
                    City: null,
                    Region: null,
                    CountryCode: 'AU',
                    PostalCode: null,
                    PhoneNumber: null,
                    FaxNumber: null,
                    Email: null,
                }),
                periodOptions: [
                    {
                        label: '1 Year',
                        value: 1
                    }
                ],
                columns: [
                    {
                        title: 'Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 260,
                    },
                    {
                        title: 'Expiry Date',
                        field: 'expiry_date',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Account', 
                        field: 'account', 
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Lock Status',
                        field: 'lock_status',
                        minWidth: 150,
                        formatter({_cell}) {
                            if (_cell.value === 2) {
                                return `<span class='badge badge-red'>Locked</span>`;
                            }

                            return `<span class='badge badge-green'>Unlocked</span>`;
                        }
                    }
                ],
                actions: [                                  
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync,
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    },
                    {
                        label: 'Renew',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: (data) => {
                            this.selected = data[0];
                            this.confirmRenew();
                        }
                    },
                    {
                        label: 'Edit Name Servers',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: (data) => {
                            this.selected = data[0];
                            this.nameServersForm.set({
                                domainId: this.selected.id,
                                host: this.selected.nameservers.map(item => { return item.name }).join('\n')
                            });
                            this.nameServersForm.errors.clear();
                            this.showEditNameServersModal = true;
                        }
                    },
                    {
                        label: 'Lock',
                        icon: () => import('@/icons/LockOutlined.vue'),
                        isEligible(items) {
                            return items[0].lock_status != 2;
                        },
                        handler: (items) => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to lock "<strong>${items[0].name}</strong>".`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, lock it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.patch(`tpp/domains/${items[0].id}/lock`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Domain was successfully locked.'
                                    })
                                }
                            })
                        }
                    },
                    {
                        label: 'Unlock',
                        icon: () => import('@/icons/LockOpenOutlined.vue'),
                        isEligible(items) {
                            return items[0].lock_status == 2;
                        },
                        handler: (items) => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to unlock "<strong>${items[0].name}</strong>".`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, unlock it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.patch(`tpp/domains/${items[0].id}/unlock`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Domain was successfully unlocked.'
                                    })
                                }
                            })
                        }
                    }
                ]
            }
        },
        computed: {
            ...mapGetters({
                items: 'tpp/domains/items',          
            }),
            regions() {
                const index = this.countries.findIndex(country => {
                    return country.countryShortCode === this.editContactForm.CountryCode;
                })
                return this.countries[index].regions;
            }
        },
        methods: {            
            add() {
                this.showRegisterModal = true;
                this.registerForm.reset();
                this.registerForm.errors.clear();
                this.domainIsAvailable = false;
            },
            checkDomainAvailability: debounce(function (domain) {
                this.checkingDomainAvailability = true;
                this.domainIsAvailable = false;
                this.registerForm.set({
                    au: false,
                    Period: null,
                });
                this.$store.dispatch('tpp/domains/check', {
                    Domain: domain
                })
                .then(({ data }) => {
                    this.checkingDomainAvailability = false;
                    this.domainIsAvailable = true;
                    this.periodOptions = [];

                    for (let i = data.minimum; i <= data.maximum; i++) {
                        let postfix = 'Year';

                        if (i > 1) {
                            postfix = 'Years';
                        }

                        this.periodOptions.push({
                            label: `${i} ${postfix}`,
                            value: i,
                        });
                    }

                    if (this.registerForm.Domain.includes('.au')) {
                        this.registerForm.set({
                            Period: 2,
                            au: true,
                        });
                    }
                })
                .catch(({ response }) => {
                    this.checkingDomainAvailability = false;
                    this.registerForm.errors.record(response.data);
                });
            }, 1000),
            store() {
                this.registeringDomain = true;
                this.registerForm.errors.clear();
                this.$store.dispatch('tpp/domains/store', this.registerForm.payload())
                    .then(() => {
                        this.registeringDomain = false;
                        this.showRegisterModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Domain was successfully registered.'
                        })
                    })
                    .catch(({ response }) => {
                        this.registeringDomain = false;
                        this.registerForm.errors.record(response.data);
                    })
            },
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                this.$store.dispatch('tpp/domains/sync')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = true;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.',
                        });
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    });
            },
            handleRowSelected(row) {
                this.showEditModal = true;
                this.selected = row.data;
            },
            updateNameServers() {
                this.updatingNameServers = true;
                this.nameServersForm.errors.clear();
                this.$store.dispatch('tpp/domains/updateNameServers', this.nameServersForm.payload())
                    .then(({data}) => {
                        this.updatingNameServers = false;
                        this.showEditNameServersModal = false;
                        this.refreshTable = true;
                        this.selected = data;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Name servers was successfully updated.'
                        });
                    })
                    .catch(({ response }) => {
                        this.updatingNameServers = false;
                        this.nameServersForm.errors.record(response.data);
                    });
            },
            editContact(type) {
                let contact = this.selected[type];

                if (type === 'registrant') {
                    contact = this.selected.owner;
                }

                if (type === 'tech') {
                    contact = this.selected.technical;
                }

                this.editContactForm.errors.clear();
                this.editContactForm.set({
                    DomainId: this.selected.id,
                    ContactType: type,
                    FirstName: contact.first_name,
                    LastName: contact.last_name,
                    OrganisationName: contact.organisation_name,
                    Address1: contact.address1,
                    Address2: contact.address2,
                    Address3: contact.address3,
                    City: contact.city,
                    Region: contact.region,
                    CountryCode: contact.country_code,
                    PostalCode: contact.postal_code,
                    Email: contact.email,
                    PhoneNumber: contact.phone_number,
                    FaxNumber: contact.fax_number,
                });
                this.showEditContactModal = true;
            },
            updateContact() {
                this.updatingContact = true;
                this.editContactForm.errors.clear();
                this.$store.dispatch('tpp/domains/updateContact', this.editContactForm.payload())
                    .then(({ data }) => {
                        this.updatingContact = false;
                        this.refreshTable = true;
                        this.showEditContactModal = false;
                        this.selected = data;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Contact was successfully updated.'
                        });
                    })
                    .catch(({ response }) => {
                        this.updatingContact = false;
                        this.editContactForm.errors.record(response.data);
                    })
            },
            confirmRenew() {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to renew "<strong>${this.selected.name}</strong>".`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, renew it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('tpp/domains/renew', {domain: this.selected.name})
                                   .catch(({ response }) => {
                                        this.$swal.showValidationMessage(response.data.message);
                                   });
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Domain was successfully renewed.'
                        })
                    }
                })
            }          
        }
    }
</script>